import { apiSlice } from './api';
import { ListParams, ResponseBody } from '../types/common';
import {
  AccountDeleteInfo,
  AccountDeleteReqBody,
  FaqItem,
  FeedbackForm,
  FeedbackItem,
  NoticeItem,
  User,
} from '../types/settings';

export const settingsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    validatePwd: builder.mutation({
      query: (data) => ({
        url: 'library/mgmt/user/validate-pwd',
        method: 'POST',
        body: { data },
      }),
      invalidatesTags: ['Settings'],
    }),
    getUser: builder.query<User, void>({
      query: () => 'library/mgmt/user',
      transformResponse: (res: ResponseBody) => res.data,
      providesTags: ['Settings'],
    }),
    updateUser: builder.mutation({
      query: (data) => ({
        url: 'library/mgmt/user',
        method: 'PUT',
        body: { data },
      }),
      invalidatesTags: ['Settings'],
    }),
    getForm: builder.query<ResponseBody, void>({
      query: () => 'library/mgmt/user/init',
      transformResponse: (res: ResponseBody) => res.data,
      providesTags: ['Settings'],
    }),
    deleteUser: builder.mutation<ResponseBody, AccountDeleteReqBody>({
      query: (data) => ({
        url: 'library/mgmt/user',
        method: 'DELETE',
        body: { data },
      }),
    }),
    getSettingsStorage: builder.query<any, void>({
      query: () => 'library/mgmt/storage',
      transformResponse: (res: ResponseBody) => res.data,
      providesTags: ['Settings'],
      forceRefetch: () => true,
    }),
    delLibResource: builder.mutation({
      query: (resourceId) => ({
        url: `library/mgmt/storage/library/${resourceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Settings'],
    }),
    delDriveResource: builder.mutation({
      query: (resourceId) => ({
        url: `library/mgmt/storage/archive/${resourceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Settings'],
    }),
    delTrashResource: builder.mutation({
      query: (resourceId) => ({
        url: `library/mgmt/storage/trash/${resourceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Settings'],
    }),
    delAllTrashResource: builder.mutation<ResponseBody, void>({
      query: () => ({
        url: `library/mgmt/storage/trash/all`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Settings'],
    }),
    getShareLinkNotification: builder.query({
      query: ({ page = 0, size = 30, sortDir = 'DESC' }) => ({
        url: 'library/mgmt/share',
        params: { page, size, sortDir },
      }),
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page && arg.page > 0) {
          currentCache.push(...newItems);
        } else {
          return newItems;
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      transformResponse: (res: ResponseBody) => res.data,
    }),
    updateShareLinkNotification: builder.mutation({
      query: (projectId) => ({
        url: `library/mgmt/share/${projectId}`,
        method: 'POST',
      }),
      invalidatesTags: ['Share'],
    }),
    deleteShareLinkNotification: builder.mutation({
      query: (projectId) => ({
        url: `library/mgmt/share/${projectId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Settings'],
    }),
    getNotice: builder.query<NoticeItem[], ListParams>({
      query: (arg) => ({ url: 'mgmt/notice', params: { ...arg } }),
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page && arg.page > 0) {
          currentCache.push(...newItems);
        } else {
          return newItems;
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      transformResponse: (res: ResponseBody) => res.data,
    }),
    getFaq: builder.query<FaqItem[], ListParams>({
      query: (arg) => ({ url: 'mgmt/faq', params: { ...arg } }),
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page && arg.page > 0) {
          currentCache.push(...newItems);
        } else {
          return newItems;
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      transformResponse: (res: ResponseBody) => res.data,
    }),
    getFeedback: builder.query<FeedbackItem[], ListParams>({
      query: (arg) => ({ url: 'mgmt/qna', params: { ...arg } }),
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page && arg.page > 0) {
          currentCache.push(...newItems);
        } else {
          return newItems;
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      transformResponse: (res: ResponseBody) => res.data,
      providesTags: ['Settings'],
    }),
    getFeedbackForm: builder.query<FeedbackForm[], void>({
      query: () => ({ url: 'mgmt/qna/init' }),
      transformResponse: (res: ResponseBody) => res.data.qnaCdList,
    }),
    AddFeedback: builder.mutation({
      query: (data) => ({
        url: 'mgmt/qna',
        method: 'POST',
        body: { data },
      }),
      invalidatesTags: ['Settings'],
    }),
    getSettingsTerms: builder.query({
      query: (agreeCd) => `library/mgmt/agree/${agreeCd}`,
      transformResponse: (res: ResponseBody) => res.data,
    }),
    getSettingsPrevTerms: builder.query({
      query: (agreeCd) => `library/mgmt/agree/${agreeCd}/prev`,
      transformResponse: (res: ResponseBody) => res.data,
    }),
    getRegistrationRequest: builder.query({
      query: ({
        page = 0,
        size = 30,
        sortBy = 'request_date',
        sortDir = 'DESC',
      }) => ({
        url: 'library/mgmt/student',
        params: { page, size, sortBy, sortDir },
      }),
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page && arg.page > 0) {
          currentCache.push(...newItems);
        } else {
          return newItems;
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      transformResponse: (res: ResponseBody) => res.data,
    }),
    updateRegistrationRequest: builder.mutation({
      query: (seq) => ({
        url: `library/mgmt/student/${seq}`,
        method: 'POST',
      }),
    }),
    deleteRegistrationRequest: builder.mutation({
      query: (seq) => ({
        url: `library/mgmt/student/${seq}`,
        method: 'DELETE',
      }),
    }),
    getAccountDelete: builder.query<AccountDeleteInfo, void>({
      query: () => ({
        url: 'library/mgmt/user/delete/init',
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),
  }),
});

export const {
  useValidatePwdMutation,
  useGetUserQuery,
  useUpdateUserMutation,
  useGetFormQuery,
  useDeleteUserMutation,
  useGetSettingsStorageQuery,
  useDelLibResourceMutation,
  useDelDriveResourceMutation,
  useDelTrashResourceMutation,
  useDelAllTrashResourceMutation,
  useGetShareLinkNotificationQuery,
  useUpdateShareLinkNotificationMutation,
  useDeleteShareLinkNotificationMutation,
  useGetNoticeQuery,
  useGetFaqQuery,
  useGetFeedbackQuery,
  useGetFeedbackFormQuery,
  useAddFeedbackMutation,
  useGetSettingsTermsQuery,
  useGetSettingsPrevTermsQuery,
  useGetRegistrationRequestQuery,
  useUpdateRegistrationRequestMutation,
  useDeleteRegistrationRequestMutation,
  useGetAccountDeleteQuery,
} = settingsApiSlice;
